import { useEffect, useMemo, useState } from "react";
import { supabase } from "../../lib/supabaseClient";
import { prpWorkspaceCommand } from "../../lib/prpWorkspace";
import {
  usePrpSessionState,
} from "../../lib/prpUiContinuity";

// LOT0F6_PRP_TOURNEE_TERRAIN_INTELLIGENTE_V1

type AnyRecord = Record<string, any>;
type Props = {
  leads: AnyRecord[];
  activities: AnyRecord[];
  onRefresh: () => Promise<void> | void;
};
type GeoPoint = { latitude: number; longitude: number };
type Provenance = GeoPoint & {
  lead_id: string;
  source_address: string;
  source_category: string;
  provider_place_id: string;
};
type Stop = {
  lead: AnyRecord;
  geo: Provenance;
  leg_km: number;
  priority_label: string;
  priority_score: number;
};
type ForfyState = {
  lead_id: string;
  loading: boolean;
  saving: boolean;
  saved: boolean;
  payload?: AnyRecord;
  error?: string;
};

const MAX_ROUTE_STOPS = 8;

const asText = (value: unknown, fallback = ""): string =>
  typeof value === "string" && value.trim() ? value.trim() : fallback;

const asNumber = (value: unknown): number | null => {
  const parsed = typeof value === "number" ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const metadataObject = (value: unknown): AnyRecord => {
  if (value && typeof value === "object" && !Array.isArray(value)) {
    return value as AnyRecord;
  }
  if (typeof value === "string" && value.trim()) {
    try {
      const parsed = JSON.parse(value);
      return parsed && typeof parsed === "object" && !Array.isArray(parsed)
        ? parsed as AnyRecord
        : {};
    } catch {
      return {};
    }
  }
  return {};
};

const validGeo = (lat: number | null, lng: number | null): boolean =>
  lat !== null && lng !== null
  && lat >= -90 && lat <= 90
  && lng >= -180 && lng <= 180;

const haversineKm = (a: GeoPoint, b: GeoPoint): number => {
  const rad = (deg: number) => deg * Math.PI / 180;
  const dLat = rad(b.latitude - a.latitude);
  const dLng = rad(b.longitude - a.longitude);
  const lat1 = rad(a.latitude);
  const lat2 = rad(b.latitude);
  const x =
    Math.sin(dLat / 2) ** 2
    + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 6371 * 2 * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x));
};

const localDay = (value: unknown): string | null => {
  if (typeof value !== "string" || !value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000)
    .toISOString().slice(0, 10);
};

const today = (): string => {
  const d = new Date();
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000)
    .toISOString().slice(0, 10);
};

const priorityFor = (lead: AnyRecord): { label: string; score: number } => {
  const due = localDay(lead.next_action_at);
  const now = today();
  const stage = asText(lead.pipeline_stage || lead.status, "new").toLowerCase();

  if (due && due < now) return { label: "Relance en retard", score: 500 };
  if (due === now) return { label: "À faire aujourd’hui", score: 450 };
  if (stage === "meeting") return { label: "Rendez-vous", score: 400 };
  if (stage === "proposal") return { label: "Proposition", score: 350 };
  if (stage === "contacted") return { label: "À relancer", score: 300 };
  if (stage === "new") return { label: "Nouveau prospect", score: 250 };
  return { label: "À qualifier", score: 100 };
};

const provenanceMap = (activities: AnyRecord[]): Map<string, Provenance> => {
  const map = new Map<string, Provenance>();
  const ordered = [...activities].sort(
    (a, b) =>
      new Date(b.created_at || b.occurred_at || 0).getTime()
      - new Date(a.created_at || a.occurred_at || 0).getTime(),
  );

  for (const activity of ordered) {
    const leadId = asText(activity.lead_id);
    if (!leadId || map.has(leadId)) continue;

    const meta = metadataObject(activity.metadata);
    if (
      meta.event !== "nearby_prospect_saved"
      || meta.source !== "google_maps_places"
      || meta.source_coordinates_authoritative !== false
      || meta.official_coordinate_truth_changed !== false
    ) {
      continue;
    }

    const lat = asNumber(meta.source_latitude);
    const lng = asNumber(meta.source_longitude);
    if (!validGeo(lat, lng)) continue;

    map.set(leadId, {
      lead_id: leadId,
      latitude: lat as number,
      longitude: lng as number,
      source_address: asText(meta.source_address),
      source_category: asText(meta.source_category),
      provider_place_id: asText(meta.provider_place_id),
    });
  }
  return map;
};

const directionsUrl = (origin: GeoPoint, stops: Stop[]): string => {
  if (!stops.length) return "";
  const last = stops[stops.length - 1];
  const params = new URLSearchParams({
    api: "1",
    origin: `${origin.latitude},${origin.longitude}`,
    destination: `${last.geo.latitude},${last.geo.longitude}`,
    travelmode: "driving",
  });
  const waypoints = stops.slice(0, -1)
    .map((s) => `${s.geo.latitude},${s.geo.longitude}`)
    .join("|");
  if (waypoints) params.set("waypoints", waypoints);
  return `https://www.google.com/maps/dir/?${params.toString()}`;
};

const stopUrl = (stop: Stop): string => {
  const params = new URLSearchParams({
    api: "1",
    query: `${stop.geo.latitude},${stop.geo.longitude}`,
  });
  if (stop.geo.provider_place_id) {
    params.set("query_place_id", stop.geo.provider_place_id);
  }
  return `https://www.google.com/maps/search/?${params.toString()}`;
};

const runForfyPreVisit = async (
  lead: AnyRecord,
  routePosition: number,
  routeCount: number,
): Promise<AnyRecord> => {
  const { data: { session }, error: sessionError } =
    await supabase.auth.getSession();

  if (sessionError) throw sessionError;
  const token = session?.access_token?.trim();
  if (!token) throw new Error("prp_session_required");

  const result = await supabase.functions.invoke(
    "prp-forfy-agent-run",
    {
      body: {
        action: "run_prp_forfy_agent",
        agent_key: "forfy_prp_qualifier",
        context: {
          business_name: asText(lead.business_name, "Prospect"),
          status: asText(lead.pipeline_stage || lead.status, "new"),
          notes: asText(lead.notes),
          next_action: asText(lead.next_action),
          route_position: routePosition,
          route_stop_count: routeCount,
          source: "prp_tournee_du_jour",
          instruction:
            "Prépare un briefing terrain concis: objectif de visite, trois questions à poser, signal d'intérêt à observer et prochaine action recommandée. N'invente aucun fait, courriel, téléphone ou contact.",
        },
      },
      headers: { Authorization: `Bearer ${token}` },
    },
  );

  if (result.error) {
    const data = result.data as AnyRecord | null;
    throw new Error(
      typeof data?.error === "string"
        ? data.error
        : result.error.message || "forfy_previsit_failed",
    );
  }

  const payload = (result.data || {}) as AnyRecord;
  if (payload.ok !== true) {
    throw new Error(asText(payload.error, "forfy_previsit_failed"));
  }
  return payload;
};

export default function PrpTourneeTerrainIntelligente({
  leads,
  activities,
  onRefresh,
}: Props) {
  const [consent, setConsent] = useState(false);
  const [locating, setLocating] = useState(false);
  const [origin, setOrigin] = useState<GeoPoint | null>(null);
  const [geoError, setGeoError] = useState("");
  const [selectedIds, setSelectedIds] =
    usePrpSessionState<string[]>(
      "route.selectedLeadIds",
      [],
      (value) =>
        Array.isArray(value)
        && value.length <= MAX_ROUTE_STOPS
        && value.every(
          (id) =>
            typeof id === "string",
        ),
    );

  const [mode, setMode] =
    usePrpSessionState<
      "priority"
      | "distance"
    >(
      "route.mode",
      "priority",
      (value) =>
        value === "priority"
        || value === "distance",
    );
  const [route, setRoute] = useState<Stop[]>([]);
  const [busyLeadId, setBusyLeadId] = useState("");
  const [forfy, setForfy] = useState<ForfyState | null>(null);

  const geoByLead = useMemo(() => provenanceMap(activities), [activities]);

  const eligible = useMemo(
    () => leads
      .map((lead) => {
        const id = asText(lead.id);
        const geo = geoByLead.get(id);
        return geo ? { lead, geo, priority: priorityFor(lead) } : null;
      })
      .filter(Boolean) as Array<{
        lead: AnyRecord;
        geo: Provenance;
        priority: { label: string; score: number };
      }>,
    [leads, geoByLead],
  );

  useEffect(
    () => {
      const eligibleIds =
        new Set(
          eligible
            .map(
              (item) =>
                asText(
                  item.lead.id,
                ),
            )
            .filter(
              Boolean,
            ),
        );

      const pruned =
        selectedIds
          .filter(
            (id) =>
              eligibleIds.has(id),
          )
          .slice(
            0,
            MAX_ROUTE_STOPS,
          );

      const changed =
        pruned.length !== selectedIds.length
        || pruned.some(
          (id, index) =>
            id !== selectedIds[index],
        );

      if (changed) {
        setSelectedIds(
          pruned,
        );

        setRoute(
          [],
        );
      }
    },
    [
      eligible,
      selectedIds,
      setSelectedIds,
    ],
  );

  const urgentCount = useMemo(
    () => eligible.filter((item) => item.priority.score >= 450).length,
    [eligible],
  );

  const totalKm = useMemo(
    () => route.reduce((sum, stop) => sum + stop.leg_km, 0),
    [route],
  );

  const usePosition = () => {
    setGeoError("");
    if (!consent) {
      setGeoError("Confirme d’abord l’utilisation temporaire de ta position.");
      return;
    }
    if (!navigator.geolocation) {
      setGeoError("La géolocalisation n’est pas disponible dans ce navigateur.");
      return;
    }

    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setOrigin({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        });
        setRoute([]);
        setLocating(false);
      },
      (error) => {
        setGeoError(error.message || "Position non disponible.");
        setLocating(false);
      },
      {
        enableHighAccuracy: false,
        timeout: 12000,
        maximumAge: 300000,
      },
    );
  };

  const toggleLead = (leadId: string) => {
    setRoute([]);
    setSelectedIds((current) => {
      if (current.includes(leadId)) return current.filter((id) => id !== leadId);
      if (current.length >= MAX_ROUTE_STOPS) return current;
      return [...current, leadId];
    });
  };

  const selectPriorities = () => {
    setRoute([]);
    setSelectedIds(
      [...eligible]
        .sort((a, b) => b.priority.score - a.priority.score)
        .slice(0, MAX_ROUTE_STOPS)
        .map((item) => asText(item.lead.id))
        .filter(Boolean),
    );
  };

  const calculateRoute = () => {
    if (!origin) {
      setGeoError("Utilise ta position avant de calculer la tournée.");
      return;
    }

    const remaining = eligible.filter((item) =>
      selectedIds.includes(asText(item.lead.id))
    );

    if (!remaining.length) {
      setGeoError("Sélectionne au moins un prospect.");
      return;
    }

    const pool = [...remaining];
    const output: Stop[] = [];
    let cursor: GeoPoint = origin;

    while (pool.length) {
      pool.sort((a, b) => {
        const da = haversineKm(cursor, a.geo);
        const db = haversineKm(cursor, b.geo);

        if (mode === "priority") {
          const priorityDelta = b.priority.score - a.priority.score;
          if (priorityDelta !== 0) return priorityDelta;
        }
        return da - db;
      });

      const next = pool.shift();
      if (!next) break;

      output.push({
        lead: next.lead,
        geo: next.geo,
        leg_km: haversineKm(cursor, next.geo),
        priority_label: next.priority.label,
        priority_score: next.priority.score,
      });
      cursor = next.geo;
    }

    setRoute(output);
    setGeoError("");
  };

  const markVisited = async (stop: Stop) => {
    const leadId = asText(stop.lead.id);
    if (!leadId) return;

    setBusyLeadId(leadId);
    try {
      const stage = asText(
        stop.lead.pipeline_stage || stop.lead.status,
        "new",
      ).toLowerCase();

      await prpWorkspaceCommand("activity_add", {
        lead_id: leadId,
        activity_type: "visit",
        summary: "Visite terrain effectuée depuis Tournée du jour.",
        next_action: "Consigner le résultat de la visite et définir la prochaine action.",
        next_action_at: new Date(
          Date.now() + 24 * 60 * 60 * 1000,
        ).toISOString(),
      });

      if (stage === "new") {
        await prpWorkspaceCommand("lead_update_status", {
          lead_id: leadId,
          status: "contacted",
        });
      }

      await Promise.resolve(onRefresh());
      window.dispatchEvent(
        new CustomEvent("forfeo:prp-workspace-refresh", {
          detail: { source: "route_visit", lead_id: leadId },
        }),
      );
    } finally {
      setBusyLeadId("");
    }
  };

  const prepareWithForfy = async (stop: Stop, index: number) => {
    const leadId = asText(stop.lead.id);
    if (!leadId) return;

    setForfy({
      lead_id: leadId,
      loading: true,
      saving: false,
      saved: false,
    });

    try {
      const payload = await runForfyPreVisit(stop.lead, index + 1, route.length);
      setForfy({
        lead_id: leadId,
        loading: false,
        saving: false,
        saved: false,
        payload,
      });
    } catch (error) {
      setForfy({
        lead_id: leadId,
        loading: false,
        saving: false,
        saved: false,
        error: error instanceof Error ? error.message : "forfy_previsit_failed",
      });
    }
  };

  const saveForfyBrief = async (stop: Stop) => {
    if (!forfy?.payload || forfy.saving || forfy.saved) return;

    const leadId = asText(stop.lead.id);
    const resultId = asText(forfy.payload.forfy_result_id);
    if (!leadId || !resultId) return;

    setForfy((current) =>
      current ? { ...current, saving: true } : current
    );

    try {
      await prpWorkspaceCommand("forfy_result_save", {
        forfy_result_id: resultId,
        agent_key: "forfy_prp_qualifier",
        lead_id: leadId,
        business_name: asText(stop.lead.business_name, "Prospect"),
        output: forfy.payload.output || {},
        runtime_meta: forfy.payload.runtime_meta || {},
        canonical_product_context_version:
          forfy.payload.canonical_product_context_version || "forfeo-products-v1",
      });

      setForfy((current) =>
        current ? { ...current, saving: false, saved: true } : current
      );
      await Promise.resolve(onRefresh());
    } catch (error) {
      setForfy((current) =>
        current
          ? {
              ...current,
              saving: false,
              error: error instanceof Error
                ? error.message
                : "forfy_memory_save_failed",
            }
          : current
      );
    }
  };

  return (
    <section
      className="rounded-3xl border border-emerald-100 bg-gradient-to-br from-white via-white to-emerald-50 p-5 shadow-sm"
      data-lot0f6-tournee-terrain="true"
    >
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <p className="text-xs font-black uppercase tracking-[0.24em] text-emerald-700">
            0F-6 · Tournée terrain intelligente
          </p>
          <h3 className="mt-1 text-xl font-black text-slate-950">
            Ma tournée du jour
          </h3>
          <p className="mt-1 max-w-3xl text-sm font-semibold leading-6 text-slate-600">
            Jusqu’à {MAX_ROUTE_STOPS} prospects, priorités CRM ou proximité,
            itinéraire Google Maps, visite CRM et briefing Forfy avant chaque arrêt.
          </p>
        </div>

        <div className="grid grid-cols-3 gap-2 text-center">
          <div className="rounded-2xl border border-slate-200 bg-white px-3 py-2">
            <p className="text-lg font-black text-slate-950">{eligible.length}</p>
            <p className="text-[10px] font-black uppercase text-slate-400">géolocalisés</p>
          </div>
          <div className="rounded-2xl border border-amber-200 bg-amber-50 px-3 py-2">
            <p className="text-lg font-black text-amber-900">{urgentCount}</p>
            <p className="text-[10px] font-black uppercase text-amber-700">urgents</p>
          </div>
          <div className="rounded-2xl border border-violet-200 bg-violet-50 px-3 py-2">
            <p className="text-lg font-black text-violet-900">{route.length}</p>
            <p className="text-[10px] font-black uppercase text-violet-700">arrêts</p>
          </div>
        </div>
      </div>

      <div className="mt-5 rounded-2xl border border-slate-200 bg-white p-4">
        <label className="flex items-start gap-3">
          <input
            checked={consent}
            className="mt-1 h-4 w-4"
            onChange={(event) => {
              const checked = event.target.checked;
              setConsent(checked);
              if (!checked) {
                setOrigin(null);
                setRoute([]);
              }
            }}
            type="checkbox"
          />
          <span>
            <span className="block text-sm font-black text-slate-900">
              J’autorise l’utilisation temporaire de ma position pour calculer ma tournée.
            </span>
            <span className="mt-1 block text-xs font-semibold leading-5 text-slate-500">
              La position reste dans cette session. Elle n’est pas enregistrée comme
              adresse, territoire ou coordonnée officielle du PRP.
            </span>
          </span>
        </label>

        <div className="mt-3 flex flex-wrap gap-2">
          <button
            className="rounded-xl bg-slate-950 px-4 py-3 text-sm font-black text-white disabled:opacity-40"
            disabled={!consent || locating}
            onClick={usePosition}
            type="button"
          >
            {locating
              ? "Localisation…"
              : origin
                ? "✓ Position temporaire prête"
                : "Utiliser ma position"}
          </button>

          <button
            className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-black text-emerald-900 disabled:opacity-40"
            disabled={eligible.length === 0}
            onClick={selectPriorities}
            type="button"
          >
            Sélectionner mes priorités
          </button>
        </div>

        {geoError && (
          <div className="mt-3 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-xs font-bold text-rose-800">
            {geoError}
          </div>
        )}
      </div>

      <div className="mt-5 grid gap-4 xl:grid-cols-[minmax(0,1fr)_400px]">
        <div>
          <div className="flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white p-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-sm font-black text-slate-900">Prospects de tournée</p>
              <p className="mt-1 text-xs font-semibold text-slate-500">
                Seulement les prospects provenant d’Autour de moi avec provenance Google temporaire.
              </p>
            </div>

            <div className="flex gap-2">
              <button
                className={`rounded-xl border px-3 py-2 text-xs font-black ${
                  mode === "priority"
                    ? "border-violet-500 bg-violet-100 text-violet-900"
                    : "border-slate-200 bg-white text-slate-600"
                }`}
                onClick={() => {
                  setMode("priority");
                  setRoute([]);
                }}
                type="button"
              >
                Priorités CRM
              </button>
              <button
                className={`rounded-xl border px-3 py-2 text-xs font-black ${
                  mode === "distance"
                    ? "border-violet-500 bg-violet-100 text-violet-900"
                    : "border-slate-200 bg-white text-slate-600"
                }`}
                onClick={() => {
                  setMode("distance");
                  setRoute([]);
                }}
                type="button"
              >
                Proximité
              </button>
            </div>
          </div>

          <div className="mt-3 grid gap-3 md:grid-cols-2">
            {eligible.map((item) => {
              const leadId = asText(item.lead.id);
              const selected = selectedIds.includes(leadId);
              return (
                <button
                  className={`rounded-2xl border p-4 text-left ${
                    selected
                      ? "border-emerald-400 bg-emerald-50"
                      : "border-slate-200 bg-white"
                  }`}
                  key={leadId}
                  onClick={() => toggleLead(leadId)}
                  type="button"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="font-black text-slate-950">
                        {asText(item.lead.business_name, "Prospect")}
                      </p>
                      <p className="mt-1 text-xs font-semibold text-slate-500">
                        {item.geo.source_address || "Adresse source Google"}
                      </p>
                    </div>
                    <span className={`rounded-full px-3 py-1 text-[10px] font-black uppercase ${
                      selected
                        ? "bg-emerald-700 text-white"
                        : "bg-slate-100 text-slate-500"
                    }`}>
                      {selected ? "Sélectionné" : "Ajouter"}
                    </span>
                  </div>

                  <div className="mt-3 flex flex-wrap gap-2">
                    <span className="rounded-full bg-amber-50 px-3 py-1 text-[10px] font-black text-amber-800">
                      {item.priority.label}
                    </span>
                    {item.geo.source_category && (
                      <span className="rounded-full bg-slate-100 px-3 py-1 text-[10px] font-black text-slate-500">
                        {item.geo.source_category}
                      </span>
                    )}
                  </div>
                </button>
              );
            })}

            {eligible.length === 0 && (
              <div className="md:col-span-2 rounded-2xl border border-dashed border-slate-300 bg-white p-8 text-center">
                <p className="font-black text-slate-700">Aucun prospect géolocalisable.</p>
                <p className="mt-2 text-sm font-semibold text-slate-400">
                  Sauvegarde d’abord des commerces depuis Autour de moi.
                </p>
              </div>
            )}
          </div>
        </div>

        <aside className="rounded-2xl border border-slate-200 bg-white p-4">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-xs font-black uppercase tracking-[0.18em] text-violet-700">
                Plan de tournée
              </p>
              <p className="mt-1 text-lg font-black text-slate-950">
                {selectedIds.length}/{MAX_ROUTE_STOPS} sélectionnés
              </p>
            </div>
            {route.length > 0 && (
              <div className="text-right">
                <p className="text-xl font-black text-emerald-800">{totalKm.toFixed(1)} km</p>
                <p className="text-[10px] font-black uppercase text-slate-400">distance approx.</p>
              </div>
            )}
          </div>

          <button
            className="mt-4 w-full rounded-xl bg-violet-700 px-4 py-3 text-sm font-black text-white disabled:opacity-40"
            disabled={!origin || selectedIds.length === 0}
            onClick={calculateRoute}
            type="button"
          >
            Calculer ma tournée
          </button>

          {route.length > 0 ? (
            <div className="mt-4">
              <a
                className="block rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-center text-sm font-black text-emerald-900"
                href={directionsUrl(origin as GeoPoint, route)}
                rel="noreferrer"
                target="_blank"
              >
                Ouvrir l’itinéraire dans Google Maps
              </a>

              <div className="mt-4 space-y-3">
                {route.map((stop, index) => {
                  const leadId = asText(stop.lead.id);
                  const currentForfy = forfy?.lead_id === leadId ? forfy : null;
                  return (
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3" key={leadId}>
                      <div className="flex items-start gap-3">
                        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-slate-950 text-sm font-black text-white">
                          {index + 1}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="font-black text-slate-950">
                            {asText(stop.lead.business_name, "Prospect")}
                          </p>
                          <p className="mt-1 text-xs font-semibold text-slate-500">
                            {stop.leg_km.toFixed(1)} km depuis l’arrêt précédent
                          </p>
                          <p className="mt-1 text-[10px] font-black uppercase text-amber-700">
                            {stop.priority_label}
                          </p>
                        </div>
                      </div>

                      <div className="mt-3 grid gap-2 sm:grid-cols-2 xl:grid-cols-1">
                        <a
                          className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-center text-xs font-black text-slate-700"
                          href={stopUrl(stop)}
                          rel="noreferrer"
                          target="_blank"
                        >
                          Voir dans Maps
                        </a>
                        <button
                          className="rounded-xl border border-violet-200 bg-violet-50 px-3 py-2 text-xs font-black text-violet-900 disabled:opacity-40"
                          disabled={currentForfy?.loading === true}
                          onClick={() => prepareWithForfy(stop, index)}
                          type="button"
                        >
                          {currentForfy?.loading ? "Forfy prépare…" : "Forfy · préparer la visite"}
                        </button>
                        <button
                          className="rounded-xl bg-slate-950 px-3 py-2 text-xs font-black text-white disabled:opacity-40 sm:col-span-2 xl:col-span-1"
                          disabled={busyLeadId === leadId}
                          onClick={() => markVisited(stop)}
                          type="button"
                        >
                          {busyLeadId === leadId ? "Enregistrement…" : "✓ Marquer comme visité"}
                        </button>
                      </div>

                      {currentForfy?.error && (
                        <div className="mt-3 rounded-xl border border-rose-200 bg-rose-50 p-2 text-xs font-bold text-rose-800">
                          {currentForfy.error}
                        </div>
                      )}

                      {currentForfy?.payload && (
                        <div className="mt-3 rounded-xl border border-violet-100 bg-white p-3">
                          <p className="text-[10px] font-black uppercase tracking-[0.16em] text-violet-700">
                            Briefing Forfy avant visite
                          </p>
                          <pre className="mt-2 max-h-56 overflow-auto whitespace-pre-wrap rounded-xl bg-slate-950 p-3 text-[11px] font-semibold text-slate-100">
                            {JSON.stringify(currentForfy.payload.output || {}, null, 2)}
                          </pre>
                          <button
                            className="mt-2 w-full rounded-xl bg-violet-700 px-3 py-2 text-xs font-black text-white disabled:opacity-50"
                            disabled={currentForfy.saving || currentForfy.saved}
                            onClick={() => saveForfyBrief(stop)}
                            type="button"
                          >
                            {currentForfy.saving
                              ? "Conservation…"
                              : currentForfy.saved
                                ? "✓ Briefing conservé dans le CRM"
                                : "Conserver le briefing dans le CRM"}
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="mt-4 rounded-xl border border-dashed border-slate-200 p-5 text-center text-xs font-bold leading-5 text-slate-400">
              Sélectionne des prospects, autorise temporairement ta position,
              puis calcule la tournée.
            </div>
          )}
        </aside>
      </div>

      <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 p-3 text-xs font-bold leading-5 text-amber-900">
        Les coordonnées utilisées proviennent de Google Places et restent des données
        temporaires de découverte. Elles ne deviennent jamais des coordonnées officielles
        Forfeo. La tournée et ta position courante ne sont pas persistées. Marquer une
        visite ou conserver un briefing Forfy nécessite toujours un clic humain.
      </div>
    </section>
  );
}
